
/**
 * Utility for detecting and recovering from blank screens,
 * particularly in the Lovable editor environment
 */

import { detectConnectionSpeed } from './networkSpeedDetector';

/**
 * Check if the screen appears to be blank (no visible content in #root)
 */
export const isBlankScreen = (): boolean => {
  const appRoot = document.getElementById('root');
  if (!appRoot) return true;
  
  // Check if there's visible content in the root element
  const hasVisibleContent = 
    appRoot.innerText.trim().length > 10 || 
    appRoot.querySelectorAll('*').length > 5;
    
  return !hasVisibleContent;
};

/**
 * Initialize blank screen recovery system
 * @param options Configuration options
 */
export const initBlankScreenRecovery = (options: {
  checkDelay?: number;
  recoveryDelay?: number;
  maxRecoveryAttempts?: number;
} = {}) => {
  // Get the connection speed to adjust timings
  const connectionSpeed = detectConnectionSpeed();
  
  // Extend delays for very slow connections
  const isVerySlow = connectionSpeed === 'very-slow';
  
  const {
    checkDelay = isVerySlow ? 4000 : 2000,
    recoveryDelay = isVerySlow ? 5000 : 3000,
    maxRecoveryAttempts = isVerySlow ? 5 : 3
  } = options;
  
  // Skip if not in Lovable editor
  if (typeof window === 'undefined' || !window.parent || window === window.parent) {
    return;
  }
  
  // Get current recovery attempts
  const currentAttempts = parseInt(sessionStorage.getItem('recovery_attempts') || '0');
  
  // Check if we've exceeded max attempts
  if (currentAttempts >= maxRecoveryAttempts) {
    console.log('[Recovery] Maximum recovery attempts reached, stopping automatic recovery');
    return;
  }
  
  // Schedule check after initial render
  setTimeout(() => {
    if (isBlankScreen()) {
      console.log(`[Recovery] Blank screen detected (connection: ${connectionSpeed}), scheduling recovery`);
      
      // Update recovery attempts
      sessionStorage.setItem('recovery_attempts', (currentAttempts + 1).toString());
      
      // Add visual indicator
      const recoveryIndicator = document.createElement('div');
      recoveryIndicator.style.position = 'fixed';
      recoveryIndicator.style.top = '0';
      recoveryIndicator.style.left = '0';
      recoveryIndicator.style.right = '0';
      recoveryIndicator.style.padding = '10px';
      recoveryIndicator.style.backgroundColor = 'rgba(255, 0, 0, 0.1)';
      recoveryIndicator.style.color = '#aa0000';
      recoveryIndicator.style.textAlign = 'center';
      recoveryIndicator.style.fontFamily = 'sans-serif';
      recoveryIndicator.style.fontSize = '14px';
      recoveryIndicator.style.zIndex = '999999';
      recoveryIndicator.innerText = `Blank screen detected${isVerySlow ? ' on slow connection' : ''}. Reloading in ${Math.round(recoveryDelay/1000)}s...`;
      document.body.appendChild(recoveryIndicator);
      
      // Add reload button
      const reloadButton = document.createElement('button');
      reloadButton.innerText = 'Reload Now';
      reloadButton.style.marginLeft = '10px';
      reloadButton.style.padding = '2px 8px';
      reloadButton.style.backgroundColor = '#aa0000';
      reloadButton.style.color = 'white';
      reloadButton.style.border = 'none';
      reloadButton.style.borderRadius = '4px';
      reloadButton.style.cursor = 'pointer';
      reloadButton.onclick = () => window.location.reload();
      recoveryIndicator.appendChild(reloadButton);
      
      // Schedule recovery
      setTimeout(() => {
        console.log('[Recovery] Executing recovery reload');
        window.location.reload();
      }, recoveryDelay);
    }
  }, checkDelay);
  
  // Add to window load event as well
  window.addEventListener('load', () => {
    setTimeout(() => {
      if (isBlankScreen()) {
        console.log('[Recovery] Blank screen detected after load event, reloading...');
        window.location.reload();
      } else {
        // Reset recovery attempts on successful load
        sessionStorage.removeItem('recovery_attempts');
      }
    }, checkDelay);
  });
};

/**
 * Call this function to enable automatic reload when blank screens are detected
 */
export const enableBlankScreenAutoRecovery = () => {
  // Only initialize in browser
  if (typeof window !== 'undefined') {
    initBlankScreenRecovery();
  }
};
